import { ROLLOUT_STATE } from '~common';
import { CAPABILITIES, CapabilityName } from '~configs/capabilities';
import { permissionRolloutConfigService } from '~services/permission-rollout-config.service';
import { CapabilityRule } from '~types/capability.type';
import { canAccessByPermission } from '~utils/can-access-by-permission.util';
import { checkRolesMatchWithUserRoles } from '~utils/common.util';
import { auditPermissionCheck } from '~utils/permission-audit-log.util';

/**
 * Rollout-aware decision for a single rule, ignoring its `requires` chain.
 * Mirrors `RolesRestrict` / `SideNavItem`: gated once ANY of the rule's keys
 * is ON (grant iff the user holds ANY of them, no legacy bypass); otherwise
 * `legacy` decides, and AUDIT keys get logged via `auditPermissionCheck`.
 */
const canByOwnRule = (rule: CapabilityRule, name: CapabilityName): boolean => {
  const keys = rule.anyOf ?? [];

  const isGated = keys.some(
    (key) =>
      permissionRolloutConfigService.getPermissionState(key) ===
      ROLLOUT_STATE.ON,
  );

  if (isGated) {
    return keys.some((key) => canAccessByPermission(key));
  }

  const { legacy } = rule;
  let legacyResult: boolean;
  if (legacy === undefined || legacy === 'allow') legacyResult = true;
  else if (typeof legacy === 'function') legacyResult = legacy();
  else legacyResult = checkRolesMatchWithUserRoles([...legacy]);

  auditPermissionCheck(name, keys, legacyResult);

  return legacyResult;
};

/**
 * `visiting` tracks the current DFS path only (removed on backtrack), so a
 * true cycle (A requires B requires A) is caught; `resolved` memoizes
 * completed decisions, so a diamond — two siblings that both require the
 * same ancestor (e.g. `pinning.submit` requiring both `pinning.venue.search`
 * and `pinning.station.search`, which both require `pinning.workflow.access`)
 * — reuses the cached result instead of tripping the cycle guard.
 */
const evaluate = (
  name: CapabilityName,
  visiting: Set<CapabilityName>,
  resolved: Map<CapabilityName, boolean>,
): boolean => {
  const cached = resolved.get(name);
  if (cached !== undefined) return cached;

  if (visiting.has(name)) {
    // eslint-disable-next-line no-console
    console.warn(
      `[capability] circular "requires" chain detected at "${name}" — denying`,
    );
    return false;
  }
  visiting.add(name);

  const rule = CAPABILITIES[name];
  const dependenciesGranted = (rule.requires ?? []).every((dependency) =>
    evaluate(dependency, visiting, resolved),
  );

  const result = dependenciesGranted && canByOwnRule(rule, name);
  visiting.delete(name);
  resolved.set(name, result);
  return result;
};

/**
 * Evaluates a capability from the central registry, including its full
 * `requires` dependency chain. Non-reactive — use the `useCan` hook inside
 * components so decisions refresh when the rollout config changes.
 */
export const canAccessCapability = (name: CapabilityName): boolean =>
  evaluate(name, new Set<CapabilityName>(), new Map<CapabilityName, boolean>());
